/**
 * What the help bubble says (help.tsx shows it).
 *
 * One topic is one job, in the order it is done, in six steps or fewer. The
 * text uses two marks and nothing else:
 *
 *   **Looks right**   a control or heading, exactly as it is written on screen
 *   [Add](/add)       a place in the app
 *
 * "Exactly as written" is what makes a step followable: the reader finds the
 * control by looking for those words. test/help.test.ts checks every bold
 * label against the page sources, so renaming a button without updating its
 * step fails `npm test`. A label the page builds at run time ("Save 3
 * decisions") cannot be checked that way and is described rather than quoted.
 *
 * The long form of all this is public/guide.md. This is the part of it that
 * fits beside the page while the job is being done.
 */

export interface HelpTopic {
  id: string;
  title: string;
  /** Offered under "How do I…". Topics that only explain one page are not. */
  listed: boolean;
  /** Steps are numbered unless this is false: then they are points, in no order. */
  ordered?: boolean;
  /** How to get to the page the job is done on. Left out when already there. */
  start?: string;
  steps: string[];
  /** What goes wrong most often, said once. */
  notes?: string[];
}

export const TOPICS: HelpTopic[] = [
  {
    id: 'receipt',
    title: 'Add a receipt or bill',
    listed: true,
    start: 'Open [Add](/add), top right.',
    steps: [
      'Press **Choose files** and pick the photo or PDF.',
      'Wait for its row to read **Saved** with a date and an amount, usually within a minute.',
      'Click the row to check it, then file it with **Looks right**.',
    ],
    notes: [
      'Each file becomes its own expense. A second document for the same expense goes under **Add a document** on that expense.',
    ],
  },
  {
    id: 'provider',
    title: 'Add a bill from a doctor, pharmacy or insurer',
    listed: true,
    steps: [
      'From their website, save the visit’s statement, receipt or explanation of benefits as a PDF: download it, or print the page to a PDF.',
      'Here, open [Add](/add), press **Choose files**, and pick one file per visit.',
      'When its row reads **Saved**, click it.',
      'Check the date is the day of care and the amount is your share after insurance. **Edit details** corrects either.',
      'If insurance paid part, press **Insurance paid part**, then **Attach the statement**.',
      'Pick who it was for and press **Looks right**.',
    ],
    notes: [
      'A bill does not show it was paid. Add the payment confirmation under **Add a document**.',
    ],
  },
  {
    id: 'amazon',
    title: 'Add Amazon purchases',
    listed: true,
    start: 'Open [Imports](/imports) and choose **Amazon orders**.',
    steps: [
      'Do the export page’s steps on Amazon’s site, including the confirmation email. The file arrives by email within a day or so.',
      'Back on the export page, press **Choose the export file** and pick the ZIP.',
      'When the import reads **Ready to review**, press **Review**, choose who the items were for, and **Accept** or **Reject** each line.',
      'Save with the button at the bottom. Accepted lines are filed; a few may wait on [Home](/) for a closer look.',
    ],
    notes: [
      'Importing again is safe: lines already in are skipped. Do not also add an order as a receipt, or it counts twice.',
    ],
  },
  {
    id: 'mileage',
    title: 'Log a drive to medical care',
    listed: true,
    start: 'Open [Add](/add) and choose **A drive to medical care**.',
    steps: [
      'Enter the **Date of the trip** and **Who it was for**.',
      'Under **Where and why**, say where you went and what for.',
      'Enter the **Round-trip miles**, and **Parking and tolls** if there were any.',
      'Press **Log the trip**. The amount is worked out at the IRS rate for that year.',
    ],
    notes: ['If the page asks for that year’s mileage rate, the account owner enters it once.'],
  },
  {
    id: 'review',
    title: 'Check and file an expense',
    listed: true,
    start: 'Open the expense from **To review** on [Home](/).',
    steps: [
      'Compare the document with the values read from it.',
      'Under **Who was it for?**, pick the person.',
      'Anything wrong or missing? Press **Edit details**. Changes save as you make them.',
      'Insurance paid part? Press **Insurance paid part** and attach the insurer’s statement.',
      'Press **Looks right** to file it.',
    ],
    notes: [
      'If **Looks right** cannot be pressed, the lines above it say what is still needed.',
      'A filed expense cannot be edited. Use **Don’t claim this** and add it again.',
    ],
  },
  {
    id: 'find',
    title: 'Find an expense or fix a mistake',
    listed: true,
    start: 'Open [Expenses](/expenses).',
    steps: [
      'Search by merchant or description, or pick a status: **To review**, **Ready to claim**, **Reimbursed** or **Not claiming**.',
      'Click a row to open it.',
      'Still to review? Press **Edit details** and correct it.',
      'Already filed? Press **Don’t claim this**, say why, and add it again with the right values.',
    ],
    notes: ['Nothing is ever deleted. Every change is listed under **History** on the expense.'],
  },
  {
    id: 'withdrawals',
    title: 'Account for money taken out of the HSA',
    listed: true,
    start: 'Press the round button, top right, and choose [Withdrawals](/withdrawals).',
    steps: [
      'Pick the **Tax year**.',
      'If the withdrawal is not listed, press **Record a withdrawal** and enter its date and amount.',
      'On the withdrawal, press **Match expenses**, tick the filed expenses it paid for, then **Match these**.',
      'Those expenses become **Reimbursed**. **Unmatch** undoes it.',
    ],
    notes: ['A withdrawal with no expenses behind it is taxable, so each one should end up matched or carry a note.'],
  },
  {
    id: 'packet',
    title: 'Build an audit packet',
    listed: true,
    start: 'Press the round button, top right, and choose [Audit packet](/packet).',
    steps: [
      'Choose **A tax year** or **A date range**.',
      'Press **Build a packet**. It takes a minute or two.',
      'When it is listed under **Built packets**, press **Download**.',
    ],
    notes: ['**Download CSV**, lower on the page, gives every record as one spreadsheet.'],
  },
  {
    id: 'settings',
    title: 'Set up the family and the HSA',
    listed: true,
    start: 'Press the round button, top right, and choose [Family & settings](/settings).',
    steps: [
      'Under **The HSA**, enter the **Date the HSA was established** and attach proof of it. Nothing dated earlier can be claimed.',
      'Under **Family**, use **Add a person** for everyone whose care you pay for.',
      'Under **Two questions a year**, press **Answer** for each tax year once its return is filed.',
    ],
    notes: ['Only the account owner can change this page.'],
  },
  {
    id: 'for-someone',
    title: 'Entering expenses for someone else',
    listed: true,
    ordered: false,
    steps: [
      'Add receipts, log drives, correct details and attach documents freely.',
      'Leave the decisions to the owner unless asked to make them: **Looks right**, **Don’t claim this**, **Accept**, **Reject**, and Family & settings.',
      'Search [Expenses](/expenses) for the same merchant, date and amount first, so nothing goes in twice.',
      'Never guess a value. Leave it empty and say so under **Notes** (**Edit details**, then **More details**).',
      'A reason or a doctor’s note must come from the owner or the doctor.',
      'Afterwards, tell the owner what you added and what each one still needs.',
    ],
  },

  // ---- Pages that are not a job by themselves -------------------------------
  {
    id: 'home',
    title: 'Home',
    listed: false,
    ordered: false,
    steps: [
      '**Ready to claim** is the total of filed expenses the HSA has not paid back yet.',
      '**To review** lists what is waiting for a look. Click a row to check it and file it.',
      '**Add**, top right, is where receipts, bills, drives and Amazon orders go in.',
      '[Trends](/trends) charts what you paid over time, by type, place or person.',
      'The round button, top right, opens Imports, Withdrawals, Audit packet, Family & settings and Help.',
    ],
  },
  {
    id: 'trends',
    title: 'Trends',
    listed: false,
    ordered: false,
    steps: [
      'Pick a period along the top: **Last 12 months**, one year, or **All time**.',
      '**Show** switches the bars between the **Amount** paid and the **Count** of expenses.',
      '**Split by** colours the bars by **Type**, **Place** or **Person**. The list under the chart ranks them, biggest first.',
      'Hover over a bar for its numbers. Click a bar, or a row in the list, to open the expenses behind it.',
      '**Ready to claim** is what was filed and not yet paid back, as it stood at the end of each month.',
      '**Your HSA** shows the balance, split into what was contributed and what it has grown by. To bring it up to date, press **Update** and copy the figures from the HSA’s website.',
    ],
    notes: ['Only filed expenses are counted, by the date of the care or purchase.'],
  },
  {
    id: 'imports',
    title: 'Imports',
    listed: false,
    ordered: false,
    steps: [
      '**Amazon orders** and **Fidelity HSA activity** each open a guide, with the upload at the bottom.',
      'Earlier imports are listed below them. **Review** opens one that is waiting for decisions.',
      '**Amazon products you rejected** lists what later imports will skip. **Restore** brings one back.',
    ],
  },
  {
    id: 'activity',
    title: 'Bring in HSA account activity',
    listed: false,
    steps: [
      'Follow the steps on this page to download the activity from the account’s website.',
      'Tick off each step with its button. The page remembers where you stopped.',
      'Press **Choose the export file** and pick the download. This works at any step.',
      'Withdrawals found in the file appear under [Withdrawals](/withdrawals), ready to match to expenses.',
    ],
  },
  {
    id: 'import-review',
    title: 'Review an import',
    listed: false,
    steps: [
      'Under **Who are these for?**, choose the person. It applies to everything you accept.',
      'On each line press **Accept** if it was a medical purchase, or **Reject** if it was not.',
      'Save the decisions with the button at the bottom.',
      'When every line has a decision, press **Finish this import**.',
    ],
    notes: [
      'Accepted lines are filed straight away.',
      'A rejected product is skipped on later imports. Undo that under **Amazon products you rejected** on [Imports](/imports).',
    ],
  },
];

/** Which topic belongs to a page. Null where the list of how-tos serves better. */
export function topicForPath(pathname: string): string | null {
  if (pathname === '/') return 'home';
  if (pathname === '/add') return 'receipt';
  if (pathname === '/mileage') return 'mileage';
  if (pathname === '/expenses') return 'find';
  if (pathname.startsWith('/expenses/')) return 'review';
  if (pathname === '/trends') return 'trends';
  if (pathname === '/imports') return 'imports';
  if (pathname.startsWith('/imports/')) return 'import-review';
  if (pathname === '/wizards/amazon') return 'amazon';
  if (pathname.startsWith('/wizards/')) return 'activity';
  if (pathname === '/withdrawals') return 'withdrawals';
  if (pathname === '/packet') return 'packet';
  if (pathname === '/settings') return 'settings';
  return null;
}
